import m from 'mithril';
import CubeIndex from '../cube/CubeIndex';
import {Delta, computeDelta, formatValue} from '../render/format';
import {isLowBase} from '../render/lowBase';
import {Cube, MarkRenderer, RenderContext, RenderSpec} from '../types';

export interface KpiItem {
    id: string;
    label: string;
    value: string;
    lowBase: boolean;
    delta?: Delta;
    /** null = neutral change, otherwise whether the change is an improvement (per `deltaGood`) */
    good?: boolean | null;
    previousLabel?: string;
    trend?: number[];
}

export function kpiModel(cube: Cube, spec: RenderSpec, ctx: RenderContext): KpiItem[] {
    const index = new CubeIndex(cube);
    const trendChannel = typeof spec.encoding.trend === 'string' ? spec.encoding.trend : undefined;
    const trendItems = trendChannel ? index.items(trendChannel) : [undefined];
    const showDelta = spec.options.showDelta !== false;
    const goodDirection = spec.options.deltaGood === 'down' ? -1 : 1;

    return cube.measures.map(measure => {
        const cells = trendItems.map(item => index.value(trendChannel && item ? {[trendChannel]: item.index} : {}, measure.id));
        const filled = cells.filter(cell => cell?.value !== null && cell?.value !== undefined);
        const current = filled[filled.length - 1];
        const item: KpiItem = {
            id: measure.id,
            label: measure.label,
            value: formatValue(current?.value, measure, ctx),
            lowBase: isLowBase(cube, current),
        };

        if (showDelta && current) {
            // Compare mode carries `previous` itself; a trend falls back to the preceding period.
            // A previous term without any respondents is no base to compare with (a count would read +n against 0).
            const compared = current.previousCount !== undefined;
            const hasPrevious = compared && (current.previousCount ?? 0) > 0
                && current.previous !== undefined && current.previous !== null;
            const previous = compared ? (hasPrevious ? current.previous : null) : filled[filled.length - 2]?.value;
            const delta = computeDelta(current.value, previous, measure, ctx);
            if (delta) {
                item.delta = delta;
                item.good = delta.sign === 0 ? null : delta.sign === goodDirection;
                if (hasPrevious && cube.meta.previousTerm) {
                    item.previousLabel = cube.meta.previousTerm.label;
                }
            }
        }
        if (trendChannel && filled.length > 1) {
            item.trend = filled.map(cell => cell!.value as number);
        }

        return item;
    });
}

function sparkline(values: number[]): m.Vnode {
    const min = Math.min(...values);
    const range = Math.max(...values) - min || 1;
    const points = values
        .map((value, i) => `${(i / (values.length - 1)) * 100},${22 - ((value - min) / range) * 20}`)
        .join(' ');

    return m('svg.kpi-trend', {viewBox: '0 0 100 24', width: 100, height: 24, preserveAspectRatio: 'none'},
        m('polyline', {points, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.5, 'vector-effect': 'non-scaling-stroke'}));
}

function deltaClass(good: boolean | null | undefined): string {
    return good === true ? 'text-success' : good === false ? 'text-danger' : 'text-muted';
}

export function kpiView(items: KpiItem[]): m.Vnode {
    return m('div.dashboard-kpi.d-flex.flex-wrap.gap-4.justify-content-around.align-items-center.h-100', items.map(item =>
        m('div.kpi-item.text-center', {'data-kpi': item.id}, [
            m('div.kpi-label.text-muted', item.label),
            m('div.kpi-value.fs-1.fw-bold' + (item.lowBase ? '.text-danger' : ''), item.value),
            item.delta && m('div.kpi-delta' + '.' + deltaClass(item.good), [
                item.delta.sign === 0 ? '' : item.delta.sign > 0 ? '▲ ' : '▼ ',
                item.delta.text,
                item.previousLabel && m('small.text-muted.ms-1', `(${item.previousLabel})`),
            ]),
            item.trend && sparkline(item.trend),
        ])));
}

const renderKpi: MarkRenderer = (cube, spec, ctx) => {
    const items = kpiModel(cube, spec, ctx);

    return {type: 'html', render: el => m.render(el, kpiView(items))};
};

export default renderKpi;
